import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { DocumentService } from '@core/api/document.service';
import { GisService } from '@core/api/gis.service';
import { KmlForestryUploadService } from '@core/api/kml-forestry-upload.service';
import { SiteService } from '@core/api/site.service';
import { SpinnerService } from '@shared/spinner/spinner.service';
import * as fromApp from '@store';
import { Document, Site } from '@shared/models';
import { ToastLevel } from '@shared/models/toast-message.module';
import { ToastService } from '@shared/services/toast.service';

export interface DialogData {
  site: Site;
}

@Component({
  selector: 'fc-dialog-import-kml',
  templateUrl: './dialog-import-kml.component.html',
  styleUrls: ['./dialog-import-kml.component.scss'],
  standalone: false
})
export class DialogImportKmlComponent implements OnInit {
  @ViewChild('kmlFileUpload', { static: false }) kmlFileUpload: ElementRef;
  kmlDocSelectForm: UntypedFormGroup;
  errorMessage = null;
  activeSite?: Site;
  documents: Document[] = null;
  fileUpload = null;
  kmlParsedUpload: any;

  constructor(
    public dialogRef: MatDialogRef<DialogImportKmlComponent>,
    readonly http: HttpClient,
    readonly formBuilder: UntypedFormBuilder,
    readonly siteService: SiteService,
    readonly documentService: DocumentService,
    readonly spinnerService: SpinnerService,
    readonly gisService: GisService,
    readonly kmlForestryUploadService: KmlForestryUploadService,
    readonly store: Store<fromApp.AppState>,
    readonly toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.activeSite = data.site;
    this.kmlDocSelectForm = this.formBuilder.group({
      kmlDocSelect: [''],
      uploadKmlFileName: '',
    });
  }

  ngOnInit() {
    let kmlDocType = this.documentService.getDocTypeId('Site KML');
    this.documents = this.activeSite.documents.filter(doc => doc.documentTypeId == kmlDocType);
  }

  validateKMLFile = (file: any) => {
    const fileExtension = file.name.substring(file.name.length - 4);
    this.spinnerService.show('app-spinner', 'Validating KML');
    switch (fileExtension) {
      case '.kml':
        this.kmlForestryUploadService
          .parseKmlDocument(file, false)
          .then(parsedKml => {
            this.kmlParsedUpload = parsedKml;
          })
          .catch(() => {
            this.uploadKmlFileNameControl.setValue('');
          })
          .finally(() => {
            this.spinnerService.hide('app-spinner');
          });
        break;
      case '.kmz':
        this.kmlForestryUploadService
          .parseKmzDocument(file, false)
          .then(parsedKml => {
            this.kmlParsedUpload = parsedKml;
          })
          .catch(() => {
            this.uploadKmlFileNameControl.setValue('');
          })
          .finally(() => {
            this.spinnerService.hide('app-spinner');
          });
        break;
      default:
        this.toastService.sendMessage('Only .kml and .kmz files are supported.', ToastLevel.WARN);
        this.uploadKmlFileNameControl.setValue('');
        this.spinnerService.hide('app-spinner');
    }
  };

  onClickKmlUpload(): void {
    const kmlFileUpload = this.kmlFileUpload.nativeElement;
    kmlFileUpload.onchange = () => {
      if (kmlFileUpload.files.length === 1) {
        this.fileUpload = kmlFileUpload.files[0];
        this.validateKMLFile(this.fileUpload);
        this.uploadKmlFileNameControl.setValue(this.fileUpload.name);
      }
    };
    kmlFileUpload.click();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  applyKML() {
    const uploadKmlFileNameValue = this.kmlDocSelectForm.get('uploadKmlFileName').value;
    const kmlDocSelectValue = this.kmlDocSelectForm.get('kmlDocSelect').value;
    this.dialogRef.close(uploadKmlFileNameValue !== '' ? this.fileUpload : kmlDocSelectValue);
  }

  get uploadKmlFileName(): string {
    return this.kmlDocSelectForm.get('uploadKmlFileName').value;
  }

  get uploadKmlFileNameControl(): AbstractControl {
    return this.kmlDocSelectForm.get('uploadKmlFileName');
  }

  isValid() {
    if (this.kmlDocSelectForm.get('kmlDocSelect').value == '' && this.uploadKmlFileName !== '') {
      return true;
    } else {
      return this.kmlDocSelectForm.get('kmlDocSelect').value !== '';
    }
  }
}
